// [목적] folio 도메인 zod 스키마 — folio-crud Spec Q-23 약속에 따라 BE/FE 공유
// [이유] FE의 MSW handler / API client / 폼 검증, BE의 컨트롤러 입력 검증이 동일 스키마 사용
// [사이드 이팩트] 본 파일 수정 시 apps/api(folio-crud) + apps/web(fe-screens-skeleton) 양쪽 회귀 검증 필요
//               fe-be-gap-tier1: listFoliosQuerySchema.sort 추가 — BE FoliosRepository.list sort 분기와 연동
//               fe-be-gap-tier2 T3: planItemSchema.icon, folioSchema.{budgetBreakdown,viewCount,author} 추가
//               folio-generation-quality: planItemSchema.timeOfDay 추가 — 영속된 시간대 밴드 조회 반영,
//                 누락 시 FE buildTimeBand startAt 파생 폴백. streamDayPlanItemSchema.timeOfDay 와 동일 enum.
//               folio-enrichment AD-1: planItemSchema.activity·tip·factInfo 추가 — 풍부화 3필드(후방호환).
//                 factInfo 는 LLM 변형 금지(AD-2·CLAUDE §4) — 원문 그대로 serializeItem 에서 주입.
//                 plan-stream.ts planItemFactInfoSchema 와 동일 구조 — 두 파일 동기 필수.
// [수정 시 고려사항] 응답 객체(serialize 결과) 매핑은 본 파일에 두지 않음 — BE 전용 (apps/api/dto/serialize.ts)
//                   sort 추가 시 FoliosRepository.list ListOpts.sort + cursor.ts v=2 분기도 반드시 동기 갱신
//                   folioSchema 확장 시 serializeFolio(단일 진입점)가 모든 응답에 신규 필드 포함해야 후방호환 유지
//                   author/budgetBreakdown nullable → join/영속 누락 시에도 parse 통과(R-2)
//                   timeOfDay nullable().optional() — DDL 18 이전 row·BE 미배포 환경에서도 parse 통과(후방호환)
//                   activity/tip/factInfo nullable().optional() — DDL 19 이전 row·미배포 환경 후방호환 유지
//                   WC-CTX-08: transitSummary nullable().optional() — transport 슬롯 미입력·DDL 미적용 환경 후방호환 유지.
//                     transitSummarySchema 는 plan-stream.ts 단일 소스 — 이 파일은 import 재사용(순환참조 주의).
//                     plan-stream.ts 가 folio.ts 를 import 하면 순환참조 발생 → plan-stream→folio 방향 import 금지.
//               M7 P1(발견 화면 서버 검색·필터): listFoliosQuerySchema 에 tags(복수)·minDurationDays·
//                 maxDurationDays 추가 — FoliosRepository.list ListOpts 와 동기 필요(같은 이름 유지).
//               M6 P2(지도 SDK 선행): planItemSchema.{location,imageUrl} 추가 — v2 Silver poi 실시간 조회.
//               M6 P2(날씨 연동): dayWeatherSchema.pop 추가 — WeatherClient 파생값, code/tempMin/tempMax 는
//                 FE WeatherLabel 계약 보존을 위해 이름·구조 불변.
//               M6 P1(budget-structuring): folioSchema.budgetBreakdown 에 perPerson·isEstimate 메타
//                 필드(둘 다 optional) 추가 — apps/api FolioPersistService 가 LLM 구조화 생성 + 규칙 기반
//                 교통비 하한을 적용해 trip_portfolios.budget_breakdown 에 영속.
//               [보안 L-3, 2026-09-17] baseFolioInput.coverImageUrl — https:// 만 허용(refine 추가).
//                 folio-persist.service.ts 가 LLM heroImageUrl 을 DB 에 직접 쓰는 경로(raw repository
//                 insert)는 이 zod 스키마를 거치지 않아 영향 없음 — 이 스키마는 HTTP 입력(POST/PATCH
//                 /folios) 경계에서만 적용됨(CLAUDE §6-5).

import { z } from 'zod';
import { transitSummarySchema } from './plan-stream.js';

// ─── 입력 enum ──────────────────────────────────────────────────────────

export const visibilityEnum = z.enum(['private', 'link', 'public']);
export const planItemKindEnum = z.enum(['place', 'meal', 'stay', 'transit', 'activity', 'note']);
export const sourceEnum = z.enum(['ai', 'manual', 'recap', 'fork']);

// ─── Folio (Create / Update / Query) ───────────────────────────────────

const baseFolioInput = {
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional().nullable(),
  vibe: z.array(z.string().min(1).max(40)).max(20).optional(),
  companions: z.string().max(100).optional().nullable(),
  budgetPerPerson: z.number().int().nonnegative().optional().nullable(),
  durationNights: z.number().int().min(0).max(60).optional().nullable(),
  durationDays: z.number().int().min(1).max(60).optional().nullable(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  visibility: visibilityEnum.optional(),
  themeColor: z.string().max(40).optional().nullable(),
  // [수정 2026-09-17 보안 L-3] coverImageUrl https 전용 강제 — mixed-content/비-http(s) 스킴(javascript:,
  // data: 등) URL 이 그대로 저장·렌더(<img src>)되는 경로 차단. 기존 http:// 픽스처는 grep 확인 결과
  // apps/api·apps/web 전량 https:// 또는 null 만 사용 중이라 영향 없음(보고서 참조).
  coverImageUrl: z
    .string()
    .url()
    .max(2000)
    .refine((u) => u.startsWith('https://'), { message: 'coverImageUrl 은 https:// 만 허용됩니다' })
    .optional()
    .nullable(),
};

export const createFolioSchema = z.object({
  ...baseFolioInput,
  source: z.enum(['ai', 'manual', 'recap']).optional(),
  days: z
    .array(
      z.object({
        dayIndex: z.number().int().min(1).max(60),
        date: z
          .string()
          .regex(/^\d{4}-\d{2}-\d{2}$/)
          .optional()
          .nullable(),
        summary: z.string().max(2000).optional().nullable(),
        items: z
          .array(
            z.object({
              kind: planItemKindEnum,
              poiId: z.string().uuid().optional().nullable(),
              title: z.string().max(200).optional().nullable(),
              note: z.string().max(2000).optional().nullable(),
              startAt: z.string().datetime().optional().nullable(),
              endAt: z.string().datetime().optional().nullable(),
              costEstimate: z.number().int().nonnegative().optional(),
            }),
          )
          .max(30)
          .optional(),
      }),
    )
    .max(10)
    .optional(),
});

export const updateFolioSchema = z.object(baseFolioInput).partial();

export const folioSortEnum = z.enum(['latest', 'likes', 'forks']);

/**
 * [목적] tags 쿼리 파라미터 정규화 — 배열(?tags=a&tags=b)과 콤마구분 문자열(?tags=a,b) 둘 다 허용.
 * [이유] M7 P1 발견 화면 서버 필터 — FE ExplorePage 필터 모달(테마 멀티셀렉트)이 배열/문자열 어느 쪽으로
 *         호출해도 동일하게 동작하도록(Express qs 파서는 반복 키를 배열로, 단일 키는 문자열로 준다).
 * [사이드 이팩트] 없음(순수 전처리). 빈 문자열/공백 토큰은 제거.
 * [수정 시 고려사항] 최대 10개로 제한 — FE THEME_OPTIONS 8개 기준 여유.
 */
const tagsQueryPreprocess = z.preprocess(
  (v) => {
    if (v == null) return undefined;
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      return v
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return v;
  },
  z.array(z.string().min(1).max(40)).max(10).optional(),
);

export const listFoliosQuerySchema = z.object({
  author: z.string().optional(),
  q: z.string().max(200).optional(),
  tag: z.string().max(40).optional(),
  /** M7 P1: 복수 태그 필터 — vibe 배열과 overlap(ANY) 매칭. tag(단수, 기존)와 병행 지원(후방호환). */
  tags: tagsQueryPreprocess,
  /** M7 P1: 여행 기간 범위 필터 — duration_days >= minDurationDays */
  minDurationDays: z.coerce.number().int().min(1).max(60).optional(),
  /** M7 P1: 여행 기간 범위 필터 — duration_days <= maxDurationDays */
  maxDurationDays: z.coerce.number().int().min(1).max(60).optional(),
  visibility: visibilityEnum.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursor: z.string().max(500).optional(),
  sort: folioSortEnum.optional(),
});

/**
 * fe-be-gap-tier2 T12(ADR-TR2-5): 공용 cursor + limit 쿼리 파라미터 스키마.
 * users.controller 4곳(me/badges·me/liked·me/forked·:handle/folios)의 수기 Math.min/max 대체.
 * z.coerce.number: 'abc'→기본값(parse 실패 → default), NaN·음수·소수 방어 일관화.
 * 동작 보존: 기본 20, 상한 50.
 */
export const cursorLimitQuerySchema = z.object({
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type FolioSort = z.infer<typeof folioSortEnum>;
export type CreateFolioInput = z.infer<typeof createFolioSchema>;
export type UpdateFolioInput = z.infer<typeof updateFolioSchema>;
export type ListFoliosQuery = z.infer<typeof listFoliosQuerySchema>;
export type CursorLimitQuery = z.infer<typeof cursorLimitQuerySchema>;

// ─── Day ────────────────────────────────────────────────────────────────

export const createDaySchema = z.object({
  dayIndex: z.number().int().min(1).max(60).optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  summary: z.string().max(2000).optional().nullable(),
  weatherCode: z.string().max(40).optional().nullable(),
  weatherTempMin: z.number().optional().nullable(),
  weatherTempMax: z.number().optional().nullable(),
});
export const updateDaySchema = createDaySchema.partial();
export type CreateDayInput = z.infer<typeof createDaySchema>;
export type UpdateDayInput = z.infer<typeof updateDaySchema>;

// ─── PlanItem (Create / Update / Reorder) ──────────────────────────────

const baseItem = {
  kind: planItemKindEnum,
  poiId: z.string().uuid().optional().nullable(),
  title: z.string().max(200).optional().nullable(),
  note: z.string().max(2000).optional().nullable(),
  startAt: z.string().datetime().optional().nullable(),
  endAt: z.string().datetime().optional().nullable(),
  costEstimate: z.number().int().nonnegative().optional(),
  locked: z.boolean().optional(),
};

export const createItemSchema = z
  .object({
    dayPlanId: z.string().uuid(),
    ord: z.number().int().min(1).max(1000).optional(),
    ...baseItem,
  })
  .refine((v) => !(v.kind === 'note' && v.poiId), {
    message: 'kind=note 일 때 poiId 는 NULL 이어야 합니다',
    path: ['poiId'],
  });

export const updateItemSchema = z
  .object(baseItem)
  .partial()
  .refine((v) => !(v.kind === 'note' && v.poiId), {
    message: 'kind=note 일 때 poiId 는 NULL 이어야 합니다',
    path: ['poiId'],
  });

export const reorderItemsSchema = z.object({
  dayPlanId: z.string().uuid(),
  itemIds: z.array(z.string().uuid()).min(1).max(30),
});

export type CreateItemInput = z.infer<typeof createItemSchema>;
export type UpdateItemInput = z.infer<typeof updateItemSchema>;
export type ReorderItemsInput = z.infer<typeof reorderItemsSchema>;

// ─── Fork ───────────────────────────────────────────────────────────────

export const forkFolioSchema = z
  .object({
    kind: z.enum(['full', 'day_import']),
    sourceDayIndex: z.number().int().min(1).max(60).optional(),
    targetPortfolioId: z.string().uuid().optional(),
  })
  .refine(
    (v) =>
      v.kind === 'full' ||
      (v.kind === 'day_import' && typeof v.sourceDayIndex === 'number' && !!v.targetPortfolioId),
    { message: 'kind=day_import 시 sourceDayIndex 와 targetPortfolioId 가 필수입니다' },
  );

export type ForkFolioInput = z.infer<typeof forkFolioSchema>;

// ─── 응답(API) 객체 zod (FE의 fetchWithEnvelope 후 parse 용) ────────────

// [목적] factInfo 사실정보 jsonb 객체 스키마 — 주소/전화/영업시간/주차/휴무/이미지URL 원문 보관
// [이유] folio-enrichment AD-1·AD-2: 사실 정보를 LLM 변형 없이 원문 그대로 직렬화하기 위해 별도 스키마 분리.
//         각 필드 nullable().optional() — TourAPI 원본 결측 허용 + 후방호환.
//         버그픽스-ENRICH: imageUrl 필드 추가 — RagCandidate.firstImage 배선(OQ-g 이미지 노출 충족).
//           plan-stream.ts planItemFactInfoSchema 와 구조 일치 유지(두 파일 동기 의무).
// [사이드 이팩트] plan_items.fact_info jsonb 컬럼(DDL 19)과 1:1 대응(jsonb 라 DDL 무변경).
//               plan-stream.ts planItemFactInfoSchema 와 동일 구조 — 두 파일 동기 필수.
//               serializeItem(apps/api/dto/serialize.ts) 이 이 스키마 대로 직렬화.
//               imageUrl 추가로 FE PlanItemEnrichedContent 썸네일 렌더 경로 활성화.
// [수정 시 고려사항] LLM 이 이 객체를 생성하면 안 됨(AD-2 무변형 원칙, CLAUDE §4).
//                   pois 컬럼(DDL 20: usetime/parking/restdate) 변경 시 동기 갱신.
//                   plan-stream.ts 의 planItemFactInfoSchema 와 항상 구조 일치 유지.
//                   imageUrl 은 TourAPI first_image 원문 URL — FE 렌더 시 null 이면 기본 이미지 폴백.
const folioItemFactInfoSchema = z.object({
  addr: z.string().nullable().optional(),
  tel: z.string().nullable().optional(),
  usetime: z.string().nullable().optional(),
  parking: z.string().nullable().optional(),
  restdate: z.string().nullable().optional(),
  /** 버그픽스-ENRICH: RagCandidate.firstImage 원문 배선 — OQ-g 이미지 노출 충족. LLM 생성 금지(AD-2). */
  imageUrl: z.string().nullable().optional(),
  /**
   * [목적] TourAPI content_id(숫자 문자열) 원문 — FE 혼잡도 카드가 GET /pois/:id/congestion 호출 키로 사용.
   * [이유] fact_info jsonb 에는 folio-persist.service.ts(RAG 영속)가 이미 contentId 키를 저장 중이었으나
   *         이 스키마가 화이트리스트에 없어 GET /folios/:id 응답에서 누락됐다(읽기 노출 추가 — DB 쓰기 로직 무변경).
   * [사이드 이팩트] plan-stream.ts planItemFactInfoSchema 와 구조 동기 필수(두 파일 동기 의무 선례 유지).
   * [수정 시 고려사항] LLM 생성 금지(AD-2 원문 무변형) — contentId 는 citation.contentId 원문 그대로.
   */
  contentId: z.string().nullable().optional(),
});

export const planItemSchema = z.object({
  id: z.string().uuid(),
  ord: z.number().int(),
  type: planItemKindEnum,
  poiId: z.string().uuid().nullable(),
  title: z.string().nullable(),
  note: z.string().nullable(),
  startAt: z.string().nullable(),
  endAt: z.string().nullable(),
  costEstimate: z.number().int(),
  locked: z.boolean(),
  /** fe-be-gap-tier2 G6: LLM 추출 emoji 아이콘. 부재/누락 시 null → FE PLAN_ITEM_EMOJI_MAP 폴백 */
  icon: z.string().nullable().optional(),
  /**
   * folio-generation-quality: 영속된 시간대 밴드(ADR-FGQ-OQ7 DDL 18 plan_items.time_of_day).
   * BE serializeItem 이 포함하여 반환. 누락 시 FE buildTimeBand 가 startAt 파생 폴백으로 밴드 결정.
   * nullable().optional(): DDL 18 이전 row·BE 미배포 환경에서도 folioSchema.parse() 통과(후방호환).
   * streamDayPlanItemSchema.timeOfDay 와 동일 enum — 4밴드(morning/noon/afternoon/evening).
   */
  timeOfDay: z.enum(['morning', 'noon', 'afternoon', 'evening']).nullable().optional(),
  /**
   * folio-enrichment AD-1: 시간대별 구체 활동 추천 (LLM 생성).
   * BE serializeItem 이 plan_items.activity 컬럼(DDL 19)에서 포함하여 반환.
   * nullable().optional(): DDL 19 이전 row·미배포 환경에서도 parse 통과(후방호환).
   */
  activity: z.string().max(300).nullable().optional(),
  /**
   * folio-enrichment AD-1: 꿀팁 (LLM 이 factInfo 데이터 근거로 생성).
   * BE serializeItem 이 plan_items.tip 컬럼(DDL 19)에서 포함하여 반환.
   * nullable().optional(): DDL 19 이전 row·미배포 환경에서도 parse 통과(후방호환).
   */
  tip: z.string().max(500).nullable().optional(),
  /**
   * folio-enrichment AD-1·AD-2: 사실정보 원문 (주소/전화/영업시간/주차/휴무).
   * LLM 변형 금지(AD-2·CLAUDE §4) — serializeItem 이 plan_items.fact_info jsonb(DDL 19) 원문 그대로 반환.
   * nullable().optional(): DDL 19 이전 row·미배포 환경에서도 parse 통과(후방호환).
   */
  factInfo: folioItemFactInfoSchema.nullable().optional(),
  /**
   * [목적] 지도 SDK 핀 좌표 — v2 Silver poi.lon/lat 실시간 조회 결과(생성 시점 스냅샷 아님).
   * [이유] M6 P2(지도 SDK 선행): plan_items 에는 좌표 컬럼이 없어 factInfo.contentId 로 v2 poi 테이블을
   *        조회해 붙인다(FoliosService.getById). RAG 로 저장되지 않은 폴리오(contentId 미보유)·poi
   *        테이블 미존재 구버전 환경 모두 null 폴백.
   * [사이드 이팩트] 없음 — 읽기 전용 파생 필드.
   * [수정 시 고려사항] nullable().optional() — BE 미배포 환경에서도 parse 통과(후방호환).
   */
  location: z.object({ lat: z.number(), lng: z.number() }).nullable().optional(),
  /**
   * [목적] v2 Silver poi.rep_image(우선)·first_image(폴백) 실시간 조회 이미지 URL.
   * [이유] M6 P2: factInfo.imageUrl(생성 시점 스냅샷)과 별개로, 최신 대표 이미지를 지도 카드에 노출하기 위함.
   * [사이드 이팩트] 없음 — 읽기 전용 파생 필드.
   * [수정 시 고려사항] nullable().optional() — BE 미배포 환경에서도 parse 통과(후방호환).
   */
  imageUrl: z.string().nullable().optional(),
});

// weather: DB numeric 컬럼(string|null) → number|null 정규화. 셋 다 null이면 weather:null.
export const dayWeatherSchema = z.object({
  code: z.string().nullable(),
  tempMin: z.number().nullable(),
  tempMax: z.number().nullable(),
  /**
   * [목적] 강수확률(%) — 기상청 단기예보 POP 카테고리, WeatherClient 실시간 조회 파생값.
   * [이유] M6 P2 날씨 연동: 기존 code/tempMin/tempMax(day_plans 컬럼 기반, 항상 null 이던 필드)에
   *        pop 만 추가 — FE WeatherLabel 계약(code/tempMin/tempMax) 은 그대로 보존(회귀 0).
   * [사이드 이팩트] 없음 — optional 이라 FE 미소비 시에도 안전.
   * [수정 시 고려사항] nullable().optional() — WEATHER_API_KEY 미설정·예보 범위 밖 환경에서도 parse 통과.
   */
  pop: z.number().nullable().optional(),
  /**
   * [목적] '오늘 기준' 추정 예보 여부 — true 면 여행 날짜 미확정으로 오늘+N일차 예보를 보여주는 중.
   * [이유] [2026-09-21] RAG 생성 폴리오는 day_plans.date 가 null 이라 날씨가 전혀 표시되지 않았다.
   *        날짜를 모른 채 예보를 노출하려면 기준 시점을 함께 알려야 사용자가 오해하지 않는다.
   * [사이드 이팩트] FE WeatherLabel 이 true 일 때 '오늘 기준' 배지를 붙인다.
   * [수정 시 고려사항] nullable().optional() — BE 미배포 환경에서도 folioSchema.parse() 통과(후방호환).
   */
  relative: z.boolean().nullable().optional(),
  /** 실제 조회에 사용한 예보 날짜 'YYYYMMDD' */
  forecastDate: z.string().nullable().optional(),
});

export const dayPlanSchema = z.object({
  id: z.string().uuid(),
  dayIndex: z.number().int(),
  date: z.string().nullable(),
  summary: z.string().nullable(),
  totalCostEstimate: z.number().int(),
  items: z.array(planItemSchema),
  weather: dayWeatherSchema.nullable().optional(),
});

/**
 * fe-be-gap-tier2 G9: 폴리오/댓글 작성자 메타 — users LEFT JOIN 으로 공급.
 * nullable: 탈퇴/익명 시 null(graceful). DDL 0(ADR-T2-3, users.handle 기존 존재).
 */
export const folioAuthorSchema = z.object({
  handle: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});

/**
 * [목적] 조회자(viewer) 관점의 좋아요/북마크 상태 — GET /folios/:id 단건 응답 전용.
 * [이유] FE 상세 화면이 별도 GET /folios/:id/like 등의 추가 호출 없이 하트/북마크 아이콘의
 *        초기 활성 상태를 즉시 렌더링할 수 있도록 단건 조회 응답에 동봉(FE-BE 사전 합의 계약 C1).
 * [사이드 이팩트] FoliosService.getById 가 로그인 사용자(actor.kind==='user')에게만 채워 반환.
 *               비로그인/익명 조회, 목록(GET /folios, liked, forked, :handle/folios) 응답에는 미포함(N+1 방지).
 * [수정 시 고려사항] optional — 필드 자체가 없을 수 있으므로 FE 는 옵셔널 체이닝으로 소비해야 함(후방호환).
 *                   목록 API 에 확장할 경우 batch 쿼리(N+1 방지) 설계 필요 — 단건과 동일하게 매 row 개별 조회 금지.
 */
export const folioViewerSchema = z.object({
  liked: z.boolean(),
  bookmarked: z.boolean(),
});

export const folioSchema = z.object({
  id: z.string().uuid(),
  ownerUserId: z.string().uuid().nullable(),
  slug: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  vibe: z.array(z.string()),
  companions: z.string().nullable(),
  budgetPerPerson: z.number().int().nullable(),
  duration: z.object({
    nights: z.number().int().nullable(),
    days: z.number().int().nullable(),
  }),
  period: z.object({
    start: z.string().nullable(),
    end: z.string().nullable(),
  }),
  visibility: visibilityEnum,
  themeColor: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  forkedFromId: z.string().uuid().nullable(),
  forkCount: z.number().int(),
  likeCount: z.number().int(),
  bookmarkCount: z.number().int(),
  commentCount: z.number().int(),
  source: sourceEnum,
  createdAt: z.string(),
  updatedAt: z.string(),
  days: z.array(dayPlanSchema).optional(),
  /**
   * fe-be-gap-tier2 G7: Folio 총합 예산 카테고리 분배. LLM budgetSchema.breakdown. 부재 시 null(FE 폴백). ADR-T2-1
   * [수정] M6 P1(budget-structuring): perPerson·isEstimate 메타 필드 추가 — 둘 다 optional 이라 이전 버전
   *        BE(메타 없이 5개 카테고리만 저장하던 시절 row)·구버전 FE 파싱 모두 후방호환(zod object 는 기본
   *        strip 이라 추가 키가 있어도 없어도 parse 성공, 신규 키만 optional 로 노출).
   *        perPerson: 사용자가 원래 예산을 1인 기준으로 말했는지 여부(금액 자체는 항상 전체 인원·전체 일정
   *        총액 — apps/api folio-persist.service.ts buildBudgetBreakdownForPersist 주석 참조).
   *        isEstimate: LLM 추정치임을 나타내는 표식(현재 항상 true — 실측 데이터 연동 전까지 상수적 의미).
   */
  budgetBreakdown: z
    .object({
      accommodation: z.number().int().nonnegative(),
      food: z.number().int().nonnegative(),
      transport: z.number().int().nonnegative(),
      activity: z.number().int().nonnegative(),
      etc: z.number().int().nonnegative(),
      perPerson: z.boolean().optional(),
      isEstimate: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  /** fe-be-gap-tier2 G8: 폴리오 조회수. 비소유자 GET /folios/:id 시 +1. ADR-T2-2. optional: BE 미배포 시 undefined 허용, 표시 시 0 이상 정수. */
  viewCount: z.number().int().nonnegative().optional(),
  /** fe-be-gap-tier2 G9: 작성자 메타. users LEFT JOIN. 탈퇴/익명 시 null. ADR-T2-3 DDL 0 */
  author: folioAuthorSchema.nullable().optional(),
  /**
   * WC-CTX-08: "이동" 요약 표시 메타 — 교통수단 모드·왕복 이동비·편도거리·출발지명.
   * [목적] 폴리오 조회 응답에 이동 요약(모드·금액·왕복)을 노출하여 FE 이동 칩/라인 렌더 지원.
   * [이유] Spec §4-C 4항 "폴리오에 이동 요약 칩/라인(모드·금액·'왕복' 명시) 노출" 요건.
   *        plan-stream.ts transitSummarySchema 단일 소스 — BE 직렬화(serializeFolio)·FE 파싱 양쪽에서 동일 스키마 소비.
   *        estimateTransitCost 반환 { mode, amountKrw, roundTrip, oneWayKm, originName } 과 1:1 대응.
   * [사이드 이팩트] FE WC-CTX-14 폴리오 표시 컴포넌트가 이 필드 유무로 이동 요약 칩/라인 조건부 렌더.
   *                BE serializeFolio(apps/api/src/modules/folio/…)가 trip_portfolios.transit_summary jsonb 컬럼
   *                또는 응답 직렬화 파생 시 이 필드를 채워야 함(WC-CTX-09 소관).
   * [수정 시 고려사항] nullable().optional() — transport 슬롯 미입력·DDL 미적용·BE 미배포 환경 모두 parse 통과(후방호환).
   *                   transitSummarySchema 변경 시 plan-stream.ts 와 동기 — folio.ts 는 import 재사용이므로 자동 반영.
   *                   folio.ts 수정 시 serializeFolio 단일 진입점(apps/api)도 동기 갱신 필요(파일 헤더 수정 고려사항 참조).
   */
  transitSummary: transitSummarySchema.nullable().optional(),
  /** 계약 C1: 조회자 관점 좋아요/북마크 상태 — 로그인 사용자의 GET /folios/:id 단건 응답에만 존재(optional). */
  viewer: folioViewerSchema.optional(),
});

export const folioListSchema = z.object({
  items: z.array(folioSchema),
  nextCursor: z.string().nullable(),
});

export type PlanItem = z.infer<typeof planItemSchema>;
export type DayWeather = z.infer<typeof dayWeatherSchema>;
export type DayPlan = z.infer<typeof dayPlanSchema>;
export type Folio = z.infer<typeof folioSchema>;
export type FolioList = z.infer<typeof folioListSchema>;
export type FolioAuthor = z.infer<typeof folioAuthorSchema>;
export type FolioViewer = z.infer<typeof folioViewerSchema>;
